<template>
  <div v-if="visible" class="kb-editor-overlay" @click.self="handleOverlayClick">
    <div class="kb-editor-modal" role="dialog" aria-modal="true" aria-labelledby="kbEditorTitle">
      <header class="kb-editor-head">
        <div class="kb-editor-head-copy">
          <span class="kb-editor-eyebrow">KNOWLEDGE BASE</span>
          <h5 id="kbEditorTitle">Edit Isi Knowledge Base</h5>
          <p :title="fileName">{{ fileName }}</p>
        </div>
        <button type="button" class="kb-editor-close" aria-label="Tutup" :disabled="saving || retrying"
          @click="requestClose">
          <span aria-hidden="true">&times;</span>
        </button>
      </header>

      <div class="kb-editor-meta">
        <div class="kb-editor-meta-item">
          <span>Tipe file</span>
          <strong>{{ fileTypeLabel }}</strong>
        </div>
        <div class="kb-editor-meta-item">
          <span>Tanggal upload</span>
          <strong>{{ uploadDateLabel }}</strong>
        </div>
        <div class="kb-editor-meta-item">
          <span>Versi konten</span>
          <strong>v{{ versionLabel }}</strong>
        </div>
        <div class="kb-editor-meta-item">
          <span>Jumlah karakter</span>
          <strong>{{ formatNumber(charCount) }}</strong>
        </div>
      </div>

      <div class="kb-editor-body">
        <!-- Loading isi dokumen (termasuk lazy extraction dokumen lama) -->
        <div v-if="mode === 'loading'" class="kb-editor-state is-loading">
          <span class="kb-editor-spinner"><i class="fa fa-spinner fa-spin"></i></span>
          <strong>Memuat isi dokumen...</strong>
          <p>
            Dokumen lama mungkin perlu diekstrak ulang dari file asli. Proses ini dapat
            memakan waktu hingga beberapa saat.
          </p>
        </div>

        <!-- Error pengambilan isi -->
        <div v-else-if="mode === 'error'" class="kb-editor-state is-error">
          <span class="kb-editor-state-icon"><i class="fa fa-exclamation-circle"></i></span>
          <strong>{{ loadErrorTitle }}</strong>
          <p>{{ loadError && loadError.message }}</p>
          <div class="kb-editor-state-actions">
            <button v-if="canRetryLoad" type="button" class="btn kb-editor-btn is-primary" :disabled="loading"
              @click="fetchContent">
              <i class="fa" :class="loading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
              Coba Lagi
            </button>
            <button type="button" class="btn kb-editor-btn" @click="close">
              Tutup
            </button>
          </div>
        </div>

        <!-- Dokumen terkunci karena job sedang berjalan -->
        <div v-else-if="mode === 'processing'" class="kb-editor-state is-processing">
          <div class="kb-editor-progress-card" :class="'is-' + statusTone">
            <div class="kb-editor-progress-head">
              <div>
                <span class="kb-editor-eyebrow">PEMROSESAN KNOWLEDGE BASE</span>
                <div class="kb-editor-progress-title">
                  <span class="kb-editor-badge" :class="'is-' + statusTone">{{ statusLabel }}</span>
                  <strong>{{ percentLabel }} selesai</strong>
                </div>
                <small>
                  {{ formatNumber(progress.completed) }} dari
                  {{ formatNumber(progress.total) }} bagian berhasil diproses
                </small>
              </div>
              <small v-if="updatedLabel" class="kb-editor-progress-time">
                Diperbarui {{ updatedLabel }}
              </small>
            </div>

            <div class="kb-editor-progress-bar">
              <span :class="'is-' + statusTone" :style="{ width: progress.percent + '%' }"></span>
            </div>

            <div class="kb-editor-progress-counts">
              <span><i class="is-success"></i>Selesai <strong>{{ formatNumber(progress.completed) }}</strong></span>
              <span><i class="is-processing"></i>Sedang diproses <strong>{{ formatNumber(progress.processing)
                  }}</strong></span>
              <span><i class="is-muted"></i>Menunggu <strong>{{ formatNumber(progress.queued) }}</strong></span>
              <span><i class="is-danger"></i>Gagal <strong>{{ formatNumber(progress.failed) }}</strong></span>
            </div>

            <div class="kb-editor-progress-foot">
              <span><strong>Tahap:</strong> {{ stageLabel || "Menunggu" }}</span>
              <span v-if="progress.total > 0">
                <strong>Total bagian:</strong> {{ formatNumber(progress.total) }}
              </span>
            </div>
            <p v-if="progressMessage" class="kb-editor-progress-message">
              {{ progressMessage }}
            </p>
            <p v-else-if="!hasProgressDetail" class="kb-editor-progress-message">
              Status pemrosesan sedang dimuat. Progress akan diperbarui otomatis.
            </p>
            <p class="kb-editor-progress-note">
              <i class="fa fa-info-circle"></i>
              Progress 100% hanya ditampilkan setelah seluruh bagian berhasil disimpan.
            </p>
          </div>
          <p class="kb-editor-hint">
            Editor dinonaktifkan sampai pemrosesan selesai. Anda boleh menutup panel ini;
            pemrosesan tetap berjalan dan dapat dipantau dari halaman Knowledge Base.
          </p>
        </div>

        <!-- Berhasil -->
        <div v-else-if="mode === 'success'" class="kb-editor-state is-success">
          <span class="kb-editor-state-icon"><i class="fa fa-check-circle"></i></span>
          <strong>Knowledge Base sudah diperbarui</strong>
          <p>
            Seluruh bagian lama telah digantikan dengan isi dokumen versi terbaru.
            Chat AI akan memakai versi ini untuk pencarian jawaban.
          </p>
          <div class="kb-editor-success-meta">
            <span>Versi konten: <strong>v{{ versionLabel }}</strong></span>
            <span>Total bagian: <strong>{{ formatNumber(progress.total) }}</strong></span>
            <span>Berhasil disimpan: <strong>{{ formatNumber(progress.completed) }}</strong></span>
          </div>
        </div>

        <!-- Gagal -->
        <div v-else-if="mode === 'failed'" class="kb-editor-state is-error">
          <span class="kb-editor-state-icon"><i class="fa fa-exclamation-triangle"></i></span>
          <strong>Pemrosesan Knowledge Base gagal</strong>
          <p>{{ failureMessage }}</p>
          <p class="kb-editor-hint">
            Versi dokumen sebelumnya tetap aktif. Isi yang Anda edit belum dipublikasikan
            dan masih tersimpan di editor ini.
          </p>
          <div class="kb-editor-progress-counts is-compact">
            <span><i class="is-success"></i>Selesai <strong>{{ formatNumber(progress.completed) }}</strong></span>
            <span><i class="is-muted"></i>Menunggu <strong>{{ formatNumber(progress.queued) }}</strong></span>
            <span><i class="is-danger"></i>Gagal <strong>{{ formatNumber(progress.failed) }}</strong></span>
          </div>
          <div class="kb-editor-state-actions">
            <button type="button" class="btn kb-editor-btn is-primary" :disabled="retrying || !canRetryJob"
              @click="retryJob">
              <i class="fa" :class="retrying ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
              {{ retrying ? "Mencoba..." : "Coba Proses Ulang" }}
            </button>
            <button type="button" class="btn kb-editor-btn" :disabled="retrying" @click="backToEditor">
              <i class="fa fa-pencil"></i> Kembali ke Editor
            </button>
          </div>
          <p v-if="retryError" class="kb-editor-inline-error">{{ retryError }}</p>
        </div>

        <!-- Editor -->
        <template v-else>
          <div v-if="pendingLocked" class="kb-editor-alert is-warning">
            <i class="fa fa-clock-o"></i>
            <span>
              Dokumen sedang diproses. Tunggu sampai pemrosesan selesai sebelum mengubah isi.
            </span>
          </div>

          <!-- <div class="kb-editor-notes" v-if="lazyExtraction"> -->
          <!-- <p>
              <i class="fa fa-info-circle"></i>
              Perubahan akan diproses ulang menjadi bagian Knowledge Base baru. Seluruh
              bagian lama akan digantikan setelah proses berhasil.
            </p>
            <p>
              <i class="fa fa-scissors"></i>
              Setiap bagian Knowledge Base maksimal 1.000 karakter. Sistem akan membagi
              dokumen secara otomatis.
            </p> -->
          <div class="kb-editor-notes" v-if="lazyExtraction">

            <p v-if="lazyExtraction">
              <i class="fa fa-refresh"></i>
              Isi dokumen diekstrak ulang dari file asli saat editor dibuka.
            </p>
          </div>

          <div v-if="editorNotice" class="kb-editor-alert is-warning">
            <i class="fa fa-exclamation-triangle"></i>
            <span>{{ editorNotice }}</span>
          </div>

          <div v-if="saveError" class="kb-editor-alert is-danger">
            <i class="fa fa-exclamation-circle"></i>
            <div>
              <span>{{ saveError.message }}</span>
              <button v-if="saveError.kind === 'conflict'" type="button" class="kb-editor-inline-link"
                :disabled="loading" @click="reloadContent">
                Muat ulang isi terbaru
              </button>
            </div>
          </div>

          <div class="kb-editor-field">
            <div class="kb-editor-field-head">
              <label for="kbEditorText">Isi Dokumen</label>
              <span class="kb-editor-counter">
                {{ formatNumber(charCount) }} karakter
                <template v-if="isDirty"> · belum disimpan</template>
              </span>
            </div>
            <textarea id="kbEditorText" ref="editorText" v-model="draftText" class="kb-editor-textarea"
              :disabled="pendingLocked || saving" spellcheck="false" wrap="soft"
              placeholder="Tulis atau tempel seluruh isi dokumen di sini..."></textarea>
            <small class="kb-editor-field-help">
              Teks dikirim apa adanya. Baris baru, spasi, dan karakter Unicode tidak diubah
              oleh frontend.
            </small>
          </div>

          <div v-if="textValidationMessage" class="kb-editor-inline-error">
            <i class="fa fa-exclamation-circle"></i> {{ textValidationMessage }}
          </div>
        </template>
      </div>

      <footer class="kb-editor-footer">
        <template v-if="mode === 'edit'">
          <span class="kb-editor-footer-note">
            <i class="fa" :class="isDirty ? 'fa-exclamation-circle' : 'fa-check-circle'"></i>
            {{ isDirty ? "Perubahan belum disimpan." : "Belum ada perubahan." }}
          </span>
          <div class="kb-editor-footer-actions">
            <button type="button" class="btn kb-editor-btn" :disabled="saving" @click="requestClose">
              Batal
            </button>
            <button type="button" class="btn kb-editor-btn is-primary" :disabled="!canSave" @click="save">
              <i class="fa" :class="saving ? 'fa-spinner fa-spin' : 'fa-cloud-upload'"></i>
              {{ saving ? "Menyimpan..." : "Simpan & Proses Ulang" }}
            </button>
          </div>
        </template>

        <template v-else-if="mode === 'processing'">
          <span class="kb-editor-footer-note">
            <i class="fa fa-spinner fa-spin"></i>
            Pemrosesan berjalan di latar belakang.
          </span>
          <div class="kb-editor-footer-actions">
            <button type="button" class="btn kb-editor-btn is-primary" @click="close">
              Tutup
            </button>
          </div>
        </template>

        <template v-else-if="mode === 'success'">
          <span class="kb-editor-footer-note is-success">
            <i class="fa fa-check-circle"></i> Versi terbaru sudah aktif.
          </span>
          <div class="kb-editor-footer-actions">
            <button type="button" class="btn kb-editor-btn is-primary" @click="close">
              Tutup
            </button>
          </div>
        </template>

        <template v-else-if="mode === 'failed'">
          <span class="kb-editor-footer-note is-danger">
            <i class="fa fa-exclamation-triangle"></i> Versi lama tetap digunakan.
          </span>
          <div class="kb-editor-footer-actions">
            <button type="button" class="btn kb-editor-btn" :disabled="retrying" @click="close">
              Tutup
            </button>
          </div>
        </template>

        <template v-else>
          <span class="kb-editor-footer-note">
            <i class="fa fa-info-circle"></i>
            {{ mode === "loading" ? "Memuat isi dokumen..." : "Editor belum siap." }}
          </span>
          <div class="kb-editor-footer-actions">
            <button type="button" class="btn kb-editor-btn" @click="close">Tutup</button>
          </div>
        </template>
      </footer>
    </div>
  </div>
</template>

<script>
import { formatLocalDate } from "@/utils/helper";
import {
  formatKbPercent,
  isKbJobActive,
  kbJobStatusMeta,
  normalizeKbJob,
  safeKbJobError,
} from "@/utils/knowledgeBaseQueue";
import {
  kbEditorCharCount,
  kbEditorContentError,
  kbEditorContentIsProcessing,
  kbEditorExpectedJobId,
  kbEditorIdempotencyKey,
  kbEditorIsDirty,
  kbEditorProgress,
  kbEditorProgressPercent,
  kbEditorSaveError,
  kbEditorStageLabel,
  kbEditorValidateText,
} from "@/utils/knowledgeBaseEditor";

const MODE_LOADING = "loading";
const MODE_ERROR = "error";
const MODE_EDIT = "edit";
const MODE_PROCESSING = "processing";
const MODE_SUCCESS = "success";
const MODE_FAILED = "failed";

export default {
  name: "KnowledgeBaseEditorModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    doc: {
      type: Object,
      default: null,
    },
    job: {
      type: Object,
      default: null,
    },
    jobLoading: {
      type: Boolean,
      default: false,
    },
    licenseId: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      mode: MODE_LOADING,
      content: null,
      originalText: "",
      draftText: "",
      loadedVersion: 0,
      loadError: null,
      saveError: null,
      retryError: "",
      editorNotice: "",
      validationError: "",
      loading: false,
      saving: false,
      retrying: false,
      requestId: 0,
      localJob: null,
      sessionJobId: "",
      initializedKey: "",
      lastTerminalKey: "",
      pendingLocked: false,
      saveStartedAt: 0,
    };
  },
  computed: {
    fileName() {
      if (this.content && this.content.filename) return this.content.filename;
      return (this.doc && this.doc.file_name) || "Dokumen Knowledge Base";
    },
    fileTypeLabel() {
      const raw = (this.content && this.content.file_type) || (this.doc && this.doc.file_type) || "";
      const value = String(raw).replace(/^\./, "").toUpperCase();
      if (value) return value;
      const name = String(this.fileName || "");
      const dot = name.lastIndexOf(".");
      return dot !== -1 ? name.slice(dot + 1).toUpperCase() : "—";
    },
    uploadDateLabel() {
      const timestamp = Number(this.doc && this.doc.create_datetime);
      if (!timestamp || timestamp <= 0) return "—";
      return formatLocalDate(timestamp) || "—";
    },
    charCount() {
      return kbEditorCharCount(this.draftText);
    },
    isDirty() {
      return kbEditorIsDirty(this.originalText, this.draftText);
    },
    canSave() {
      if (this.mode !== MODE_EDIT) return false;
      if (this.saving || this.pendingLocked) return false;
      if (this.validationError) return false;
      return this.isDirty && !kbEditorValidateText(this.draftText);
    },
    textValidationMessage() {
      if (this.mode !== MODE_EDIT) return "";
      if (this.validationError) return this.validationError;
      if (!this.isDirty) return "";
      return kbEditorValidateText(this.draftText);
    },
    currentJob() {
      return this.localJob || this.job || null;
    },
    progress() {
      return kbEditorProgress(this.currentJob);
    },
    progressPercent() {
      return kbEditorProgressPercent(this.currentJob);
    },
    percentLabel() {
      return formatKbPercent(this.progressPercent);
    },
    statusLabel() {
      if (!this.currentJob) return "Memuat status";
      return kbJobStatusMeta(this.currentJob.status).label;
    },
    statusTone() {
      return kbJobStatusMeta(this.currentJob && this.currentJob.status).tone;
    },
    stageLabel() {
      const job = this.currentJob;
      if (!job) return "";
      return kbEditorStageLabel(job.current_stage || job.stage);
    },
    progressMessage() {
      const job = this.currentJob;
      if (!job) return "";
      if (job.message) return job.message;
      if (isKbJobActive(job.status)) {
        return "Pemrosesan sedang berjalan. Progress diperbarui otomatis.";
      }
      return "";
    },
    hasProgressDetail() {
      return !!this.progress.total;
    },
    updatedLabel() {
      const job = this.currentJob;
      if (!job || !job.updated_at) return "";
      const date = new Date(job.updated_at);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    versionLabel() {
      const job = this.currentJob;
      if (this.mode === MODE_SUCCESS && job && job.content_version) {
        return String(job.content_version);
      }
      return String(this.loadedVersion || 0);
    },
    loadErrorTitle() {
      const kind = this.loadError && this.loadError.kind;
      if (kind === "source_missing") return "Dokumen asli tidak dapat ditemukan";
      if (kind === "extraction") return "Ekstraksi dokumen gagal";
      if (kind === "forbidden" || kind === "auth") return "Akses dokumen ditolak";
      if (kind === "not_found") return "Dokumen tidak ditemukan";
      if (kind === "empty") return "Isi dokumen tidak tersedia";
      if (kind === "network") return "Gagal menghubungi server";
      return "Isi dokumen belum berhasil dimuat";
    },
    canRetryLoad() {
      const kind = this.loadError && this.loadError.kind;
      return kind !== "forbidden" && kind !== "auth" && kind !== "not_found" && kind !== "empty";
    },
    lazyExtraction() {
      return !!(this.content && (this.content.lazy_extraction || this.content.source === "s3_lazy_extraction"));
    },
    failureMessage() {
      const job = this.currentJob;
      if (!job) return "Pemrosesan Knowledge Base belum berhasil.";
      return safeKbJobError(job) || job.message || "Pemrosesan Knowledge Base belum berhasil.";
    },
    canRetryJob() {
      const job = this.currentJob;
      if (!job) return false;
      return job.status === "failed" || job.status === "cancelled";
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.initialize();
      } else {
        this.reset();
      }
    },
    doc(value, oldValue) {
      if (!this.visible || !value) return;
      const nextKey = String(value.id);
      const previousKey = oldValue ? String(oldValue.id) : "";
      if (nextKey !== previousKey) this.initialize();
    },
    job(value) {
      this.handleJobUpdate(value);
    },
  },
  mounted() {
    if (this.visible) this.initialize();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    formatNumber(value) {
      const number = Number(value);
      if (!isFinite(number)) return "0";
      return String(Math.round(number)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    reset() {
      this.requestId++;
      this.mode = MODE_LOADING;
      this.content = null;
      this.originalText = "";
      this.draftText = "";
      this.loadedVersion = 0;
      this.loadError = null;
      this.saveError = null;
      this.retryError = "";
      this.editorNotice = "";
      this.validationError = "";
      this.loading = false;
      this.saving = false;
      this.retrying = false;
      this.localJob = null;
      this.sessionJobId = "";
      this.initializedKey = "";
      this.lastTerminalKey = "";
      this.pendingLocked = false;
      this.saveStartedAt = 0;
    },
    initialize() {
      if (!this.doc || !this.doc.id) return;
      const key = String(this.doc.id);
      if (this.initializedKey === key) return;
      this.reset();
      this.initializedKey = key;
      // Tahap A: isi dokumen selalu dimuat lebih dulu. Job dari cache tidak
      // boleh membuat modal stuck di processing sebelum konten terbaca.
      this.mode = MODE_LOADING;
      this.fetchContent();
    },
    async fetchContent() {
      if (!this.doc || !this.doc.id) return;
      const requestId = ++this.requestId;
      const startedAt = Date.now();
      this.loading = true;
      this.mode = MODE_LOADING;
      this.loadError = null;
      this.validationError = "";
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseFileContent", {
          file_id: this.doc.id,
          license_id: this.licenseId,
        });
        if (requestId !== this.requestId) return;
        const data = (response && response.data) || {};
        this.content = data;
        this.loadedVersion = Number(data.content_version) || 0;
        this.originalText = data.text === null || data.text === undefined ? "" : String(data.text);
        this.draftText = this.originalText;

        // Hanya job yang benar-benar aktif yang boleh mengunci editor.
        // `pending_job_id` yang menunjuk job terminal (stale) tidak dihitung.
        const isProcessing = kbEditorContentIsProcessing(data);
        this.pendingLocked = isProcessing;
        if (typeof console !== "undefined" && console.info) {
          console.info("[kb-editor] content", {
            file_id: this.doc.id,
            duration_ms: Date.now() - startedAt,
            source: data.source || "",
            lazy_extraction: data.lazy_extraction === true,
            chars: this.originalText.length,
            content_version: this.loadedVersion,
            pending_job_id: data.pending_job_id || "",
            pending_job_status: data.pending_job_status || "",
            is_processing: isProcessing,
            running_job_id: data.running_job_id || "",
            running_job_status: data.running_job_status || "",
          });
        }
        this.$emit("content-loaded", data);

        if (this.pendingLocked) {
          // Job aktif boleh dilampirkan lebih awal jika parent sudah punya
          // detailnya. Jika belum, parent akan mengirim update job.
          const expectedJobId = kbEditorExpectedJobId(data);
          if (
            this.job &&
            this.job.job_id &&
            (!expectedJobId || String(this.job.job_id) === String(expectedJobId))
          ) {
            this.sessionJobId = this.job.job_id;
            this.localJob = this.job;
          } else {
            this.sessionJobId = "";
            this.localJob = null;
          }
          this.mode = MODE_PROCESSING;
          return;
        }

        if (!this.originalText.trim()) {
          this.mode = MODE_ERROR;
          this.loadError = {
            kind: "empty",
            message:
              "Isi dokumen kosong atau tidak dapat diekstrak. Unggah ulang dokumen agar isinya dapat diedit.",
          };
          return;
        }

        // Job sukses/failed lama atau cache job aktif yang sudah tidak valid
        // tidak boleh mengubah tampilan editor.
        this.localJob = null;
        this.sessionJobId = "";
        this.mode = MODE_EDIT;
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.mode = MODE_ERROR;
        this.loadError = kbEditorContentError(error);
        if (typeof console !== "undefined" && console.warn) {
          console.warn("[kb-editor] content error", {
            file_id: this.doc.id,
            duration_ms: Date.now() - startedAt,
            status: error && error.response ? error.response.status : 0,
            message: this.loadError && this.loadError.message,
          });
        }
        this.$emit("content-error", this.loadError);
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    reloadContent() {
      if (!this.doc) return;
      this.initializeForReload();
    },
    initializeForReload() {
      this.requestId++;
      this.content = null;
      this.originalText = "";
      this.draftText = "";
      this.loadedVersion = 0;
      this.loadError = null;
      this.saveError = null;
      this.retryError = "";
      this.editorNotice = "";
      this.validationError = "";
      this.pendingLocked = false;
      this.fetchContent();
    },
    handleJobUpdate(job) {
      if (!job || !job.job_id) return;
      // Jangan biarkan job dari cache mengubah modal saat konten masih dimuat.
      if (!this.content) return;
      if (this.doc && job.file_id && String(job.file_id) !== String(this.doc.id)) return;
      if (!this.sessionJobId) {
        const expectedJobId = kbEditorExpectedJobId(this.content);
        const hasActiveProcess = kbEditorContentIsProcessing(this.content);
        if (!expectedJobId && !hasActiveProcess) return;
        if (expectedJobId && String(job.job_id) !== String(expectedJobId)) return;
        if (isKbJobActive(job.status)) {
          this.sessionJobId = job.job_id;
          this.localJob = job;
          this.pendingLocked = true;
          this.mode = MODE_PROCESSING;
        }
        return;
      }
      if (String(job.job_id) !== String(this.sessionJobId)) return;
      this.localJob = job;
      if (isKbJobActive(job.status)) {
        this.pendingLocked = true;
        this.mode = MODE_PROCESSING;
        return;
      }
      this.pendingLocked = false;
      if (job.status === "success") {
        this.mode = MODE_SUCCESS;
        this.emitTerminal("success", job);
        return;
      }
      if (job.status === "failed" || job.status === "cancelled" || job.status === "partial_success") {
        this.mode = MODE_FAILED;
        this.emitTerminal("failed", job);
      }
    },
    emitTerminal(kind, job) {
      const key = kind + ":" + job.job_id + ":" + job.status;
      if (this.lastTerminalKey === key) return;
      this.lastTerminalKey = key;
      if (kind === "success") {
        this.$emit("job-success", job);
      } else {
        this.$emit("job-failed", job);
      }
    },
    attachJob(rawJob, options) {
      const job = normalizeKbJob(rawJob);
      if (!job) return null;
      const settings = options || {};
      this.localJob = job;
      this.sessionJobId = job.job_id;
      this.$emit("job-created", job);
      if (settings.duplicate) {
        this.editorNotice =
          "Permintaan sebelumnya masih diproses atau sudah selesai. Sistem memakai job yang sama.";
      }
      if (isKbJobActive(job.status)) {
        this.pendingLocked = true;
        this.mode = MODE_PROCESSING;
      } else if (job.status === "success") {
        this.pendingLocked = false;
        this.mode = MODE_SUCCESS;
        this.emitTerminal("success", job);
      } else if (job.status === "failed" || job.status === "cancelled") {
        this.pendingLocked = false;
        this.mode = MODE_FAILED;
        this.emitTerminal("failed", job);
      }
      return job;
    },
    async save() {
      if (this.saving || this.mode !== MODE_EDIT) return;
      this.validationError = "";
      this.saveError = null;
      this.editorNotice = "";

      const validation = kbEditorValidateText(this.draftText);
      if (validation) {
        this.validationError = validation;
        return;
      }
      if (!this.isDirty) {
        this.$fire(this.swalOptions({
          title: "Tidak ada perubahan",
          text: "Isi dokumen sama dengan versi yang tersimpan. Tidak ada yang perlu diproses ulang.",
          type: "info",
          timer: 2400,
          showConfirmButton: false,
        }));
        return;
      }

      const confirmed = await this.confirmSave();
      if (!confirmed) return;
      this.saving = true;
      this.saveStartedAt = Date.now();
      const idempotencyKey = kbEditorIdempotencyKey();
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseUpdateContent", {
          file_id: this.doc.id,
          text: this.draftText,
          content_version: this.loadedVersion,
          idempotency_key: idempotencyKey,
        });
        const data = (response && response.data) || {};
        const job = this.attachJob(data, { duplicate: !!data.duplicate_job });
        if (!job) {
          this.saveError = {
            kind: "invalid",
            message: "Respons job dari server tidak valid. Muat ulang status pemrosesan.",
          };
        }
      } catch (error) {
        const info = kbEditorSaveError(error);
        this.saveError = info;
        if (info.kind === "network" || info.kind === "server") {
          await this.recoverJobAfterUncertainResponse();
        }
      } finally {
        this.saving = false;
      }
    },
    async confirmSave() {
      // Guard double click: dialog konfirmasi hanya boleh satu pada satu waktu.
      if (this.saving) return false;
      this.saving = true;
      try {
        const confirmed = await this.$fire(this.swalOptions({
          title: "Proses ulang seluruh isi Knowledge Base?",
          text:
            "Seluruh bagian Knowledge Base lama akan digantikan setelah proses berhasil. " +
            "Isi dokumen yang sedang dipakai Chat AI tetap aktif sampai versi baru selesai diproses.",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Ya, proses ulang",
          cancelButtonText: "Batal",
          confirmButtonColor: "#fe9b44",
        }));
        return !!confirmed && confirmed.value === true;
      } finally {
        this.saving = false;
      }
    },
    async recoverJobAfterUncertainResponse() {
      // Request bisa saja sudah membuat job tetapi response timeout. Periksa
      // job terbaru agar pengguna tidak membuat job duplikat.
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseLatestJob", {
          file_id: this.doc.id,
          license_id: this.licenseId,
        });
        const job = normalizeKbJob(response && response.data);
        if (!job) return;
        const versionAdvanced = Number(job.content_version) > Number(this.loadedVersion);
        const likelyOurs = isKbJobActive(job.status)
          ? versionAdvanced || job.job_trigger === "edit"
          : versionAdvanced;
        if (!likelyOurs) return;
        this.sessionJobId = job.job_id;
        this.localJob = job;
        this.saveError = null;
        this.$emit("job-created", job);
        if (isKbJobActive(job.status)) {
          this.mode = MODE_PROCESSING;
        } else if (job.status === "success") {
          this.mode = MODE_SUCCESS;
          this.emitTerminal("success", job);
        } else if (job.status === "failed" || job.status === "cancelled") {
          this.mode = MODE_FAILED;
          this.emitTerminal("failed", job);
        }
      } catch (error) {
        // Biarkan pesan "tidak dapat memastikan job dibuat" tampil.
      }
    },
    async retryJob() {
      if (this.retrying) return;
      const job = this.currentJob;
      if (!job || !this.canRetryJob) return;
      this.retrying = true;
      this.retryError = "";
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseRetryJob", {
          job_id: job.job_id,
        });
        const next = this.attachJob((response && response.data) || {});
        if (!next) {
          this.retryError = "Respons job retry tidak valid.";
        }
      } catch (error) {
        const info = kbEditorSaveError(error);
        this.retryError = info.message || "Job belum berhasil dimasukkan kembali ke antrean.";
      } finally {
        this.retrying = false;
      }
    },
    backToEditor() {
      this.pendingLocked = false;
      this.mode = MODE_EDIT;
      this.saveError = null;
      this.editorNotice =
        "Pemrosesan versi sebelumnya gagal. Perubahan yang Anda buat masih ada di editor dan belum dipublikasikan.";
      this.$nextTick(() => {
        const element = this.$refs.editorText;
        if (element && element.focus) element.focus();
      });
    },
    requestClose() {
      if (this.saving || this.retrying) return;
      if (this.mode === MODE_EDIT && this.isDirty) {
        this.confirmDiscard();
        return;
      }
      this.close();
    },
    async confirmDiscard() {
      const confirmed = await this.$fire(this.swalOptions({
        title: "Tutup editor?",
        text: "Perubahan yang belum disimpan akan hilang. Job pemrosesan yang sudah dijalankan tetap berjalan.",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, tutup",
        cancelButtonText: "Kembali",
        confirmButtonColor: "#fe9b44",
      }));
      if (!confirmed || confirmed.value !== true) return;
      this.close();
    },
    handleOverlayClick() {
      this.requestClose();
    },
    close() {
      this.$emit("close");
    },
    swalOptions(options) {
      const base = options || {};
      return Object.assign({}, base, {
        customClass: Object.assign({}, base.customClass || {}, {
          container: "waajo-swal-above-modal",
        }),
        customContainerClass: "waajo-swal-above-modal",
      });
    },
  },
};
</script>

<style scoped>
.kb-editor-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 22px 16px;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.56);
}

.kb-editor-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 980px;
  max-height: calc(100vh - 44px);
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
}

.kb-editor-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #eef1f5;
}

.kb-editor-head-copy {
  min-width: 0;
}

.kb-editor-eyebrow {
  display: block;
  color: #98a2b3;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.kb-editor-head h5 {
  margin: 3px 0 3px;
  color: #172033;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.kb-editor-head p {
  max-width: 720px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #667085;
  font-size: 12px;
}

.kb-editor-close {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  font-size: 22px;
  line-height: 1;
}

.kb-editor-close:hover:not(:disabled) {
  background: #fff1ed;
  color: #b42318;
}

.kb-editor-close:disabled {
  opacity: 0.45;
}

.kb-editor-meta {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  padding: 12px 22px;
  border-bottom: 1px solid #eef1f5;
  background: #f8fafc;
}

.kb-editor-meta-item span {
  display: block;
  color: #98a2b3;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kb-editor-meta-item strong {
  display: block;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #344054;
  font-size: 12px;
  font-weight: 700;
}

.kb-editor-body {
  flex: 1 1 auto;
  padding: 18px 22px;
  overflow-y: auto;
}

.kb-editor-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 36px 18px;
  text-align: center;
  color: #667085;
  font-size: 12px;
  line-height: 1.55;
}

.kb-editor-state strong {
  color: #172033;
  font-size: 14px;
  font-weight: 800;
}

.kb-editor-state p {
  max-width: 620px;
  margin: 0;
}

.kb-editor-state-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #f8fafc;
  color: #98a2b3;
  font-size: 22px;
}

.kb-editor-state.is-error .kb-editor-state-icon {
  background: #fef2f2;
  color: #dc2626;
}

.kb-editor-state.is-success .kb-editor-state-icon {
  background: #f0fdf4;
  color: #16a34a;
}

.kb-editor-spinner {
  color: #fe9b44;
  font-size: 30px;
}

.kb-editor-state-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
}

.kb-editor-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 8px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 9px;
  background: #fff;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
}

.kb-editor-btn:hover:not(:disabled) {
  border-color: #f6c26b;
  background: #fffaf1;
  color: #b45309;
}

.kb-editor-btn.is-primary {
  border-color: #fe9b44;
  background: #fe9b44;
  color: #fff;
  box-shadow: 0 6px 14px rgba(254, 155, 68, 0.24);
}

.kb-editor-btn.is-primary:hover:not(:disabled) {
  border-color: #e77f25;
  background: #e77f25;
  color: #fff;
}

.kb-editor-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.kb-editor-notes {
  padding: 11px 13px;
  margin-bottom: 12px;
  border: 1px solid #f6d49b;
  border-radius: 9px;
  background: #fffaf1;
  color: #8f5600;
  font-size: 11.5px;
  line-height: 1.55;
}

.kb-editor-notes p {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 0 0 4px;
}

.kb-editor-notes p:last-child {
  margin-bottom: 0;
}

.kb-editor-notes i {
  margin-top: 3px;
  color: #e77f25;
}

.kb-editor-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.kb-editor-alert.is-warning {
  border: 1px solid #f6d49b;
  background: #fffaf1;
  color: #8f5600;
}

.kb-editor-alert.is-danger {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b42318;
}

.kb-editor-alert>div {
  min-width: 0;
}

.kb-editor-inline-link {
  display: inline-block;
  margin-left: 6px;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 11px;
  font-weight: 800;
  text-decoration: underline;
  padding: 0;
}

.kb-editor-field-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

.kb-editor-field-head label {
  margin: 0;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}

.kb-editor-counter {
  color: #7b8ba3;
  font-size: 11px;
  font-weight: 700;
}

.kb-editor-textarea {
  display: block;
  width: 100%;
  min-height: 340px;
  max-height: 52vh;
  padding: 12px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  background: #fff;
  color: #1d2939;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.65;
  resize: vertical;
  outline: none;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.kb-editor-textarea:focus {
  border-color: #f6c26b;
  box-shadow: 0 0 0 3px rgba(254, 155, 68, 0.18);
}

.kb-editor-textarea:disabled {
  background: #f8fafc;
  color: #98a2b3;
}

.kb-editor-field-help {
  display: block;
  margin-top: 6px;
  color: #98a2b3;
  font-size: 10.5px;
  line-height: 1.5;
}

.kb-editor-inline-error {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 9px;
  color: #b42318;
  font-size: 11.5px;
  line-height: 1.5;
}

.kb-editor-progress-card {
  width: 100%;
  max-width: 720px;
  padding: 14px 16px;
  border: 1px solid #e7edf5;
  border-radius: 12px;
  background: #fff;
  text-align: left;
}

.kb-editor-progress-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.kb-editor-progress-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 3px 0 2px;
}

.kb-editor-progress-title strong {
  font-size: 13px;
}

.kb-editor-progress-head small {
  color: #7b8ba3;
  font-size: 11px;
}

.kb-editor-progress-time {
  flex: 0 0 auto;
}

.kb-editor-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  border: 1px solid #e7edf5;
  background: #f8fafc;
  color: #667085;
}

.kb-editor-badge.is-processing {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.kb-editor-badge.is-success {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #15803d;
}

.kb-editor-badge.is-danger {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.kb-editor-progress-bar {
  height: 9px;
  margin: 10px 0 9px;
  overflow: hidden;
  border-radius: 999px;
  background: #eef1f5;
}

.kb-editor-progress-bar span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #1d4ed8;
  transition: width 0.25s ease;
}

.kb-editor-progress-bar span.is-success {
  background: #16a34a;
}

.kb-editor-progress-bar span.is-danger {
  background: #dc2626;
}

.kb-editor-progress-bar span.is-muted {
  background: #98a2b3;
}

.kb-editor-progress-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  color: #475467;
  font-size: 10.5px;
}

.kb-editor-progress-counts.is-compact {
  justify-content: center;
  margin-top: 8px;
}

.kb-editor-progress-counts span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.kb-editor-progress-counts i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #98a2b3;
}

.kb-editor-progress-counts i.is-success {
  background: #16a34a;
}

.kb-editor-progress-counts i.is-processing {
  background: #2563eb;
}

.kb-editor-progress-counts i.is-danger {
  background: #dc2626;
}

.kb-editor-progress-counts i.is-muted {
  background: #cbd5e1;
}

.kb-editor-progress-foot {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-top: 8px;
  color: #667085;
  font-size: 10.5px;
}

.kb-editor-progress-foot strong {
  color: #344054;
}

.kb-editor-progress-message {
  margin: 8px 0 0;
  padding: 7px 9px;
  border-radius: 7px;
  background: #f8fafc;
  color: #475467;
  font-size: 11px;
  line-height: 1.5;
}

.kb-editor-progress-note {
  margin: 8px 0 0;
  color: #98a2b3;
  font-size: 10.5px;
  line-height: 1.5;
}

.kb-editor-hint {
  max-width: 620px;
  margin: 10px 0 0;
  color: #98a2b3;
  font-size: 11px;
  line-height: 1.55;
}

.kb-editor-success-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 18px;
  margin-top: 4px;
  color: #667085;
  font-size: 11.5px;
}

.kb-editor-success-meta strong {
  color: #15803d;
}

.kb-editor-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 22px;
  border-top: 1px solid #eef1f5;
  background: #fff;
}

.kb-editor-footer-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #98a2b3;
  font-size: 10.5px;
  font-weight: 700;
}

.kb-editor-footer-note.is-success {
  color: #15803d;
}

.kb-editor-footer-note.is-danger {
  color: #b42318;
}

.kb-editor-footer-actions {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}

@media (max-width: 900px) {
  .kb-editor-meta {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .kb-editor-overlay {
    padding: 10px 8px;
  }

  .kb-editor-modal {
    max-height: calc(100vh - 20px);
    border-radius: 10px;
  }

  .kb-editor-head,
  .kb-editor-body,
  .kb-editor-footer {
    padding-left: 14px;
    padding-right: 14px;
  }

  .kb-editor-meta {
    grid-template-columns: 1fr 1fr;
    padding-left: 14px;
    padding-right: 14px;
  }

  .kb-editor-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .kb-editor-footer-actions {
    justify-content: flex-end;
  }

  .kb-editor-textarea {
    min-height: 260px;
    max-height: 44vh;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/settings/components/KnowledgeBaseEditorModal.vue